import { useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput, ClientPaths } from "../client";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";
import {
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { PROJECT_KEY } from "../projects/project";
const rebates = {
  useQueryAll,
  mutations: {
    useCreateViaUpload,
    usePatch,
    useDelete,
    usePostReceipt,
  },
} as const;

export default rebates;

export type SnakeCaseRebateSummary = ClientOutput["rebates"]["RebateSummary"];
export const ALL_PROJECTS_KEY = "ALL_PROJECTS_FOR_ACCOUNT";

export const PORTFOLIO_REBATES_QUERY_KEY = "portfolio-rebates";

// endpoint requires {feature: "Rebates", access: "read"}
function useQueryAll(
  account_id?: number,
  { enabled = true }: { enabled?: boolean } = { enabled: true }
) {
  return useQueryWithAccessCheckLog(
    ["Rebates", "read"],
    async (checkFeatureAccessHeader) => {
      if (typeof account_id !== "number") throw new Error("No account ID");
      const { data, error } = await FetchClients.rebates.GET("/", {
        params: { query: { account_id } },
        headers: checkFeatureAccessHeader,
      });
      if (data) return data;
      throw new Error(error);
    },
    {
      enabled: typeof account_id === "number" && enabled,
      queryKey: [PORTFOLIO_REBATES_QUERY_KEY, account_id],
    }
  );
}

type Variables = { file: File } & Pick<
  ClientPaths["d3"]["/rebate/upload"]["post"]["parameters"]["query"],
  "project_id" | "vendor_id" | "site_id"
>;
// endpoint requires {feature: "Rebates", access: "edit"}
function useCreateViaUpload(
  account_id?: number,
  onSettled?: (
    data: { id: number } | undefined,
    error: unknown,
    variables: Variables,
    context: unknown
  ) => void | Promise<unknown>
) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Rebates", "edit"],
    async (
      { file, vendor_id, project_id, site_id }: Variables,
      checkFeatureAccessHeader
    ) => {
      if (typeof account_id !== "number") throw new Error("No account ID");
      const { data, error } = await FetchClients.d3.POST("/rebate/upload", {
        params: {
          query: {
            account_id,
            filename: file.name,
            vendor_id,
            project_id,
            site_id,
          },
        },
        // @ts-expect-error - https://bractlet.atlassian.net/browse/INNO-786
        body: file,
        bodySerializer: (body) => body,
        headers: checkFeatureAccessHeader,
      });
      if (data) return data as unknown as { id: number };
      throw new Error(error);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [PORTFOLIO_REBATES_QUERY_KEY, account_id],
        });
        // A rebate appears on its project as a reward, so refresh the projects
        // cache. Not scoped to the site: when a rebate moves between sites, only
        // the new site is in the variables, which would leave the old one stale.
        // The projects portfolio summary is deliberately not invalidated -- it is
        // observed from the Rebates page, so it would fire a real request to
        // refresh a count nothing here shows, and it refetches on navigation.
        queryClient.invalidateQueries({ queryKey: [PROJECT_KEY] });
      },
      ...(onSettled ? { onSettled } : {}),
    }
  );
}

export type EditVariables = ClientOutput["rebates"]["RebateEditReq"] & {
  id?: number;
};
// endpoint requires {feature: "Rebates", access: "edit"}
function usePatch(account_id: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Rebates", "edit"],
    async (variables: EditVariables, checkFeatureAccessHeader) => {
      const { id, ...body } = variables;
      if (typeof id !== "number") throw new Error("No rebate ID");
      const { response, error } = await FetchClients.rebates.PATCH(
        "/{rebate_id}",
        {
          body,
          params: { path: { rebate_id: id } },
          headers: checkFeatureAccessHeader,
        }
      );
      if (response.ok) return;
      if (isBackpackResponseErrorBody(error)) {
        throw new BackpackServiceError(error);
      }
      throw new Error(error);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [PORTFOLIO_REBATES_QUERY_KEY, account_id],
        });
        // A rebate appears on its project as a reward, so refresh the projects
        // cache. Not scoped to the site: when a rebate moves between sites, only
        // the new site is in the variables, which would leave the old one stale.
        // The projects portfolio summary is deliberately not invalidated -- it is
        // observed from the Rebates page, so it would fire a real request to
        // refresh a count nothing here shows, and it refetches on navigation.
        queryClient.invalidateQueries({ queryKey: [PROJECT_KEY] });
      },
    }
  );
}

// endpoint requires {feature: "Rebates", access: "edit"}
function useDelete(accountId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Rebates", "edit"],
    async (rebate_ids: number[], checkFeatureAccessHeader) => {
      const { response, error } = await FetchClients.rebates.DELETE("/", {
        params: { query: { rebate_ids } },
        headers: checkFeatureAccessHeader,
      });
      if (response.ok) return;
      throw new Error(error);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [PORTFOLIO_REBATES_QUERY_KEY, accountId],
        });
        queryClient.invalidateQueries({ queryKey: [PROJECT_KEY] });
      },
    }
  );
}

// upload receipt to existing rebate
// endpoint requires {feature: "Rebates", access: "edit"}
function usePostReceipt() {
  return useMutationWithAccessCheckLog(
    ["Rebates", "edit"],
    async (
      {
        rebate_id,
        file,
      }: {
        rebate_id: number;
        file: File;
      },
      checkFeatureAccessHeader
    ) => {
      const { data, error } = await FetchClients.d3.POST(
        "/rebate-receipt/upload",
        {
          params: { query: { rebate_id, filename: file.name } },
          // @ts-expect-error - https://bractlet.atlassian.net/browse/INNO-786
          body: file,
          bodySerializer: (body) => body,
          parseAs: "text",
          headers: checkFeatureAccessHeader,
        }
      );
      if (data) return data;
      throw new Error(error);
    }
  );
}
